import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { useModalA11y } from '@/hooks/useModalA11y'
import { Backdrop } from './Backdrop'
import { Button } from './Button'

export interface DrawerProps {
  open: boolean
  onClose: () => void
  children: ReactNode
  /** Optional visible title rendered in the drawer header. */
  title?: string
  /** Accessible label when no visible title is provided. */
  ariaLabel?: string
  /**
   * When true, the dimmed backdrop is not rendered and the root overlay becomes
   * click-through (`pointer-events-none`) so the page behind stays interactive —
   * only the panel itself captures pointer events. Close via Escape or the [×]
   * button (there is no backdrop to click). Defaults to false (modal backdrop).
   */
  hideBackdrop?: boolean
}

/**
 * Right-side drawer on desktop (lg+); snaps to a bottom sheet on mobile (<lg).
 *
 * Desktop: fixed panel slides in from the right at z-50 over a z-40 backdrop.
 * Mobile: bottom sheet ~85vh with a drag-handle affordance, same z-50 / z-40 ladder.
 *
 * Z-ladder: backdrop z-40 → drawer z-50 → QuickActionBar z-[60] → CommandPalette z-[100].
 * Backdrop carries pointer-events-auto so clicks don't fall through to the page.
 * Escape closes, body scroll is locked while open, focus is trapped inside.
 * Returns null when closed (no DOM cost).
 */
export function Drawer({ open, onClose, children, title, ariaLabel, hideBackdrop = false }: DrawerProps) {
  const { t: tc } = useTranslation('common')

  // Escape close + scroll lock + full focus trap (focus-in on open, Tab/Shift+Tab
  // cycle, restore on close).  The returned ref is attached to the panel element.
  const panelRef = useModalA11y({
    open,
    onClose,
    escapeToClose: true,
    scrollLock: true,
    focusTrap: true,
  })

  if (!open) return null

  return (
    <div
      className={['fixed inset-0 z-40', hideBackdrop ? 'pointer-events-none' : ''].filter(Boolean).join(' ')}
      role="dialog"
      aria-modal={hideBackdrop ? undefined : 'true'}
      aria-label={ariaLabel ?? title}
    >
      {/* Backdrop — pointer-events-auto blocks click-through; click/tap closes.
          Omitted when hideBackdrop: the page behind stays interactive. */}
      {!hideBackdrop && <Backdrop onClick={onClose} />}

      {/*
        Panel:
          Mobile (<lg):  bottom sheet — slides up from the bottom, ~85vh, rounded top.
          Desktop (lg+): right-side panel — fixed 480px wide, slides in from the right, full height.
      */}
      <div
        ref={panelRef as React.RefObject<HTMLDivElement>}
        tabIndex={-1}
        className={[
          // Shared
          'absolute bg-base-100 shadow-xl overflow-y-auto outline-none',
          // Mobile (bottom sheet)
          'bottom-0 inset-x-0 rounded-t-2xl max-h-[85vh]',
          'pb-[max(env(safe-area-inset-bottom),1rem)]',
          // Desktop (right drawer) — overrides mobile positioning
          'lg:inset-x-auto lg:right-0 lg:top-0 lg:bottom-0 lg:h-full',
          'lg:w-[480px] lg:max-h-none lg:rounded-none lg:rounded-l-2xl',
          // z above backdrop
          'z-50',
          // Re-enable pointer events when the root is click-through (hideBackdrop)
          hideBackdrop ? 'pointer-events-auto' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {/* Mobile drag handle — hidden on desktop */}
        <div className="flex justify-center pt-3 pb-1 lg:hidden" aria-hidden="true">
          <div className="h-1 w-10 rounded-full bg-base-300" />
        </div>

        {/* Header (shown when a title is provided) */}
        {title && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-base-200">
            <h2 className="font-semibold text-base">{title}</h2>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              shape="circle"
              onClick={onClose}
              aria-label={tc('actions.close')}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Content */}
        <div className="p-4">{children}</div>
      </div>
    </div>
  )
}
